import type { RouteRecordRaw } from 'vue-router'
import {
  canUserAccessModule,
  guardApiUsers,
  guardDataClients,
  guardDataDegrees,
  guardDataInvoicingInformations,
  guardDataJobs,
  guardDataTeams,
  guardDataUserGroups,
  guardDataOffices,
  guardDataResources,
  guardDataRooms,
  guardDataSkills,
  guardProfiles,
  guardConsumption,
  guardOpenReplay,
  guardIntegrations,
  guardPersonalisation,
  guardSessions,
  guardUsers,
} from '@/config/router/navigationsGuards/guardSuperAdmin'

const MODULE_NAME = 'SUPER_ADMIN'
const MODULE_ID = 'SUPER_ADMIN'

const defaultMeta = {
  requiresAuth: true,
  adminAccess: true,
}

const prefix = '/super-admin'

const superAdminRoutes: Array<RouteRecordRaw> = [
  {
    path: '/user-management',
    meta: {
      ...defaultMeta,
    },
    beforeEnter: [() => canUserAccessModule],
    children: [
      {
        path: 'users',
        name: 'SuperAdminUsers',
        component: () => import('@/views/superAdmin/users/List.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.users',
        },
        beforeEnter: [guardUsers],
      },
      {
        path: 'profiles',
        name: 'SuperAdminProfiles',
        component: () => import('@/views/superAdmin/users/profile/ProfileList.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.profiles',
        },
        beforeEnter: [guardProfiles],
      },
      {
        path: 'profiles/:profileId',
        name: 'SuperAdminProfile',
        component: () => import('@/views/superAdmin/users/profile/ProfileItem.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.profile',
        },
        beforeEnter: [guardProfiles],
      },
      {
        path: 'sessions',
        name: 'SuperAdminSessions',
        component: () => import('@/views/superAdmin/sessions/Sessions.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.sessions',
        },
        beforeEnter: [guardSessions],
      },
      {
        path: 'api-users',
        name: 'SuperAdminApiUsers',
        component: () => import('@/views/superAdmin/apiUsers/ApiUsers.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.apiUsers',
        },
        beforeEnter: [guardApiUsers],
      },
    ],
  },
  {
    path: '/firm-management',
    meta: {
      ...defaultMeta,
    },
    beforeEnter: [() => canUserAccessModule],
    children: [
      {
        path: 'personalisation',
        name: 'SuperAdminPersonalisation',
        component: () => import('@/views/superAdmin/firm/Personnalisation.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.personalisation',
        },
        beforeEnter: [guardPersonalisation],
      },
      {
        path: 'consumption',
        name: 'Consumption',
        component: () => import('@/views/superAdmin/firm/Consumption.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.consumption',
        },
        beforeEnter: [guardConsumption],
      },
      {
        path: 'open-replay',
        name: 'SuperAdminOpenReplay',
        component: () => import('@/views/superAdmin/firm/OpenReplay.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.openReplay',
        },
        beforeEnter: [guardOpenReplay],
      },
      {
        path: 'integrations',
        name: 'SuperAdminIntegrations',
        component: () => import('@/views/superAdmin/integrations/Integrations.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.integrations',
        },
        beforeEnter: [guardIntegrations],
      },
      {
        path: 'integrations/:provider',
        name: 'SuperAdminIntegration',
        component: () => import('@/views/superAdmin/integrations/IntegrationItem.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.integration',
        },
        beforeEnter: [guardIntegrations],
      },
    ],
  }, {
    path: '/data-management',
    meta: {
      ...defaultMeta,
    },
    beforeEnter: [() => canUserAccessModule],
    children: [
      {
        path: 'job',
        name: 'Job',
        component: () => import('@/views/superAdmin/data/Job.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.jobs',
        },
        beforeEnter: [guardDataJobs],
      },
      {
        path: 'client',
        name: 'Client',
        component: () => import('@/views/superAdmin/data/Client.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.clients',
        },
        beforeEnter: [guardDataClients],
      },
      {
        path: 'skill',
        name: 'Skill',
        component: () => import('@/views/superAdmin/data/Skill.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.skills',
        },
        beforeEnter: [guardDataSkills],
      },
      {
        path: 'degree',
        name: 'Degree',
        component: () => import('@/views/superAdmin/data/Degree.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.degrees',
        },
        beforeEnter: [guardDataDegrees],
      },
      {
        path: 'office',
        name: 'Office',
        component: () => import('@/views/superAdmin/data/Office.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.offices',
        },
        beforeEnter: [guardDataOffices],
      },
      {
        path: 'room',
        name: 'Room',
        component: () => import('@/views/superAdmin/data/Room.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.rooms',
        },
        beforeEnter: [guardDataRooms],
      },
      {
        path: 'resources',
        name: 'DataResources',
        component: () => import('@/views/superAdmin/data/ResourceUpload.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.resources',
        },
        beforeEnter: [guardDataResources],
      },
      {
        path: 'invoicing-information',
        name: 'InvoicingInformation',
        component: () => import('@/views/superAdmin/data/InvoicingInformation.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.invoicingInformations',
        },
        beforeEnter: [guardDataInvoicingInformations],
      },
      {
        path: 'organization-structure',
        name: 'OrganizationStructure',
        component: () => import('@/views/superAdmin/data/OrganizationStructure.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.organizationStructure',
        },
        beforeEnter: [guardDataTeams],
      },
      {
        path: 'organization-groups',
        name: 'OrganizationGroups',
        component: () => import('@/views/superAdmin/data/OrganizationGroups.vue'),
        meta: {
          ...defaultMeta,
          titleTranslationKey: 'pageTitle.superAdmin.organizationGroups',
        },
        beforeEnter: [guardDataUserGroups],
      },
    ],
  },
  {
    path: '/alert-management',
    meta: {
      ...defaultMeta,
    },
    children: [
      {
        path: 'builder',
        redirect: { name: 'AlertBuilder' },
      },
    ],
  },
]

export default superAdminRoutes.map(route => ({ ...route, path: prefix + route.path, meta: { ...route.meta, moduleName: MODULE_NAME, moduleId: MODULE_ID } }))
